import React, { useState, useEffect, useRef, useCallback, Fragment } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { toast } from '../../components/toast/CustomToast'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import Modal from '../../components/common/Modal'
import CompanySelect from '../../components/common/CompanySelect'
import CompanyConfigsModal from '../../components/common/CompanyConfigsModal'
import Pagination from '../../components/common/Pagination'
import MonacoEditor from '../../components/common/MonacoEditor'
import HeadersConfig from '../../components/common/HeadersConfig'
import api from '../../utils/api'
import { eventAPI, supplierAPI, setupAPI } from '../../utils/apiHelpers'
import { config } from '../../config/environment'

const CompaniesPage = () => {
    const { token } = useAuth();
    const [companies, setCompanies] = useState([]);
    const [integrations, setIntegrations] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isIntegrationModalOpen, setIsIntegrationModalOpen] = useState(false);
    const [isTokenModalOpen, setIsTokenModalOpen] = useState(false);
    const [isTokenValidationModalOpen, setIsTokenValidationModalOpen] = useState(false);
    const [editingCompany, setEditingCompany] = useState(null);
    const [selectedCompanyForIntegrations, setSelectedCompanyForIntegrations] = useState(null);
    const [selectedCompanyForTokens, setSelectedCompanyForTokens] = useState(null);
    const [formData, setFormData] = useState({ cnpj: '', razao_social: '', nome_fantasia: '', active: true, core_id: null });
    const [formErrors, setFormErrors] = useState({});
    const [selectedIntegrationIds, setSelectedIntegrationIds] = useState([]);
    const [apiTokens, setApiTokens] = useState([]);
    const [newToken, setNewToken] = useState(null);
    const [tokenValidationInput, setTokenValidationInput] = useState('');
    const [tokenValidationResult, setTokenValidationResult] = useState(null);
    const [isValidatingToken, setIsValidatingToken] = useState(false);
    const [isUsersModalOpen, setIsUsersModalOpen] = useState(false);
    const [selectedCompanyForUsers, setSelectedCompanyForUsers] = useState(null);
    const [companyUsers, setCompanyUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [associatedSearchTerm, setAssociatedSearchTerm] = useState('');
    const [openActionsMenuId, setOpenActionsMenuId] = useState(null);
    const [showCompaniesTable, setShowCompaniesTable] = useState(true);
    const companiesTableScrollRef = useRef(null);
    const [integrationSearchTerm, setIntegrationSearchTerm] = useState('');
    const [integrationSupplierFilter, setIntegrationSupplierFilter] = useState('');
    const [expandedSuppliers, setExpandedSuppliers] = useState({});

    // Database management state
    const [isDbModalOpen, setIsDbModalOpen] = useState(false);
    const [selectedCompanyForDb, setSelectedCompanyForDb] = useState(null);
    const [dbStatus, setDbStatus] = useState(null);
    const [dbLoading, setDbLoading] = useState(false);
    const [dbInitializing, setDbInitializing] = useState(false);
    const [activationStatus, setActivationStatus] = useState({}); // { company_id: boolean }

    // Núcleo deletion flow: a núcleo can only be deleted after all its associated companies are disassociated
    const [coreDeleteTarget, setCoreDeleteTarget] = useState(null);
    const [coreDeleteSelectedIds, setCoreDeleteSelectedIds] = useState([]);
    const [coreDeleteProcessing, setCoreDeleteProcessing] = useState(false);

    // Company configs state
    const [isConfigsModalOpen, setIsConfigsModalOpen] = useState(false);
    const [selectedCompanyForConfigs, setSelectedCompanyForConfigs] = useState(null);

    // Activation invite state
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteEmailError, setInviteEmailError] = useState('');
    const [inviteEmailTouched, setInviteEmailTouched] = useState(false);
    const [inviteSending, setInviteSending] = useState(false);

    // Funções de formatação CNPJ
    const formatCNPJ = (cnpj) => {
        if (!cnpj) return '';
        const cleaned = cnpj.replace(/\D/g, '');
        if (cleaned.length !== 14) return cnpj;
        return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    };

    const removeCNPJMask = (cnpj) => {
        return cnpj.replace(/\D/g, '');
    };

    // Filter companies based on search term
    const filteredCompanies = companies.filter(company => {
        const searchLower = searchTerm.toLowerCase();
        const cnpjWithoutMask = removeCNPJMask(company.cnpj);
        const cnpjWithMask = formatCNPJ(company.cnpj);
        const searchWithoutMask = removeCNPJMask(searchTerm);

        const hasLetters = /[a-zA-Z]/.test(searchTerm);

        return (
            company.id.toLowerCase().includes(searchLower) ||
            company.razao_social.toLowerCase().includes(searchLower) ||
            (company.nome_fantasia || '').toLowerCase().includes(searchLower) ||
            (!hasLetters && searchWithoutMask.length > 0 && cnpjWithoutMask.includes(searchWithoutMask)) ||
            (!hasLetters && cnpjWithMask.includes(searchTerm))
        );
    });

    const handleCNPJChange = (e) => {
        const value = removeCNPJMask(e.target.value);
        setFormData({ ...formData, cnpj: value });
    };

    const isValidCNPJ = (cnpj) => {
        const cleaned = removeCNPJMask(cnpj);

        // Verifica se tem 14 dígitos
        if (cleaned.length !== 14 || /^(\d)\1{13}$/.test(cleaned)) {
            return false;
        }

        // Calcula primeiro dígito verificador
        let sum = 0;
        let multiplier = 5;
        for (let i = 0; i < 12; i++) {
            sum += parseInt(cleaned[i]) * multiplier;
            multiplier = multiplier === 2 ? 9 : multiplier - 1;
        }
        let remainder = sum % 11;
        let firstDigit = remainder < 2 ? 0 : 11 - remainder;

        // Calcula segundo dígito verificador
        sum = 0;
        multiplier = 6;
        for (let i = 0; i < 12; i++) {
            sum += parseInt(cleaned[i]) * multiplier;
            multiplier = multiplier === 2 ? 9 : multiplier - 1;
        }
        sum += firstDigit * 2;
        remainder = sum % 11;
        let secondDigit = remainder < 2 ? 0 : 11 - remainder;

        // Verifica se os dígitos calculados correspondem aos informados
        return parseInt(cleaned[12]) === firstDigit && parseInt(cleaned[13]) === secondDigit;
    };

    useEffect(() => {
        if (token) {
            fetchCompanies();
            fetchIntegrations();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Close the mobile actions menu when clicking outside of it
    useEffect(() => {
        if (!openActionsMenuId) return;
        const handleClickOutside = (e) => {
            if (!e.target.closest('[data-actions-menu]')) {
                setOpenActionsMenuId(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openActionsMenuId]);

    // Switch between the table and the card list based on real overflow: the table
    // stays mounted (invisible, out of flow) even when the cards are shown, so we can
    // keep comparing its actual scrollWidth against the available width at all times.
    useEffect(() => {
        const el = companiesTableScrollRef.current;
        if (!el) return;

        const checkOverflow = () => {
            setShowCompaniesTable(el.scrollWidth <= el.clientWidth);
        };

        checkOverflow();

        const observer = new ResizeObserver(checkOverflow);
        observer.observe(el);
        window.addEventListener('resize', checkOverflow);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', checkOverflow);
        };
    }, [filteredCompanies]);

    const fetchIntegrations = async () => {
        try {
            const response = await api.get('/integrations', {
                params: { page: 1, page_size: 100 }
            });
            setIntegrations(response.data.items || []);
        } catch (error) {

        }
    };

    const fetchCompanies = async () => {
        try {
            const response = await api.get('/companies', {
                params: { page: 1, page_size: 100 }
            });
            const companiesData = response.data.items || [];
            setCompanies(companiesData);

            // Fetch activation status for each company
            const statusMap = {};
            for (const company of companiesData) {
                try {
                    const response = await api.get(`/companies/${company.id}/database`);
                    statusMap[company.id] = response.data.has_record === true;
                } catch (error) {
                    statusMap[company.id] = false; // Company is not activated
                }
            }
            setActivationStatus(statusMap);
        } catch (error) {
            toast.error('Erro ao carregar empresas');
        } finally {
        }
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.cnpj) errors.cnpj = 'CNPJ é obrigatório';
        else if (!isValidCNPJ(formData.cnpj)) errors.cnpj = 'CNPJ inválido';
        if (!formData.razao_social) errors.razao_social = 'Razão Social é obrigatória';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            if (formData.cnpj && !isValidCNPJ(formData.cnpj)) {
                toast.error('CNPJ inválido');
            }
            return;
        }
        try {
            if (editingCompany) {
                await api.put(`/companies/${editingCompany.id}`, formData);
            } else {
                await api.post('/companies', formData);
            }
            toast.success(editingCompany ? 'Empresa atualizada' : 'Empresa criada');
            setIsModalOpen(false);
            setFormData({ cnpj: '', razao_social: '', nome_fantasia: '', active: true, core_id: null });
            setEditingCompany(null);
            fetchCompanies();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao salvar empresa');
        } finally {
        }
    };

    const handleDelete = (company) => {
        if ((company.associated_companies || []).length > 0) {
            openCoreDeleteModal(company);
            return;
        }
        toast.confirm(
            'Tem certeza?',
            async () => {
                try {
                    await api.delete(`/companies/${company.id}`);
                    toast.success('Empresa deletada');
                    fetchCompanies();
                } catch (error) {
                    toast.error(error.response?.data?.detail || 'Erro ao deletar empresa');
                } finally {
                }
            },
            () => {
                // Cancel - do nothing
            }
        );
    };

    const openCoreDeleteModal = (company) => {
        setCoreDeleteTarget(company);
        setCoreDeleteSelectedIds((company.associated_companies || []).map(c => c.id));
    };

    const closeCoreDeleteModal = () => {
        if (coreDeleteProcessing) return;
        setCoreDeleteTarget(null);
        setCoreDeleteSelectedIds([]);
    };

    const toggleCoreDeleteId = (id) => {
        setCoreDeleteSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const toggleAllCoreDelete = () => {
        const all = (coreDeleteTarget?.associated_companies || []).map(c => c.id);
        setCoreDeleteSelectedIds(coreDeleteSelectedIds.length === all.length ? [] : all);
    };

    const handleDisassociateSelected = async () => {
        if (!coreDeleteTarget || coreDeleteSelectedIds.length === 0) return;
        setCoreDeleteProcessing(true);
        try {
            const response = await api.post(`/companies/${coreDeleteTarget.id}/disassociate`, { company_ids: coreDeleteSelectedIds });
            const remaining = response.data.associated_companies || [];
            toast.success(`${coreDeleteSelectedIds.length} empresa(s) desassociada(s)`);
            fetchCompanies();
            setCoreDeleteTarget(remaining.length === 0 ? null : response.data);
            setCoreDeleteSelectedIds([]);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao desassociar empresas');
        } finally {
            setCoreDeleteProcessing(false);
        }
    };

    const handleDisassociateAllAndDelete = async () => {
        if (!coreDeleteTarget) return;
        setCoreDeleteProcessing(true);
        try {
            await api.post(`/companies/${coreDeleteTarget.id}/disassociate`, {
                company_ids: coreDeleteTarget.associated_companies.map(c => c.id)
            });
            await api.delete(`/companies/${coreDeleteTarget.id}`);
            toast.success('Empresas desassociadas e núcleo excluído');
            setCoreDeleteTarget(null);
            setCoreDeleteSelectedIds([]);
            fetchCompanies();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao excluir núcleo');
            fetchCompanies();
        } finally {
            setCoreDeleteProcessing(false);
        }
    };

    const openEditModal = (company) => {
        setEditingCompany(company);
        setAssociatedSearchTerm('');
        setFormData({ cnpj: company.cnpj, razao_social: company.razao_social, nome_fantasia: company.nome_fantasia || '', active: company.active, core_id: company.core_id || null });
        setIsModalOpen(true);
    };

    const openNewModal = () => {
        setEditingCompany(null);
        setFormData({ cnpj: '', razao_social: '', nome_fantasia: '', active: true, core_id: null });
        setFormErrors({});
        setIsModalOpen(true);
    };

    const groupIntegrationsBySupplier = (integrations) => {
        return integrations.reduce((acc, integration) => {
            const supplier = integration.supplier;
            const key = supplier ? supplier.id : 'no-supplier';
            const supplierName = supplier ? supplier.name : 'Fornecedor não especificado';
            const logoUrl = supplier ? supplier.logo_url : null;

            if (!acc[key]) {
                acc[key] = { name: supplierName, logo_url: logoUrl, integrations: [] };
            }
            acc[key].integrations.push(integration);
            return acc;
        }, {});
    };

    const toggleSupplierExpansion = (supplierId) => {
        setExpandedSuppliers(prev => ({
            ...prev,
            [supplierId]: !prev[supplierId]
        }));
    };

    const toggleSupplierIntegrations = (supplierId, integrations, checked) => {
        const integrationIds = integrations.map(i => i.id);
        if (checked) {
            setSelectedIntegrationIds([...new Set([...selectedIntegrationIds, ...integrationIds])]);
        } else {
            setSelectedIntegrationIds(selectedIntegrationIds.filter(id => !integrationIds.includes(id)));
        }
    };

    const openIntegrationModal = (company) => {
        setSelectedCompanyForIntegrations(company);
        setSelectedIntegrationIds((company.integrations || []).map(i => i.id));
        setIntegrationSearchTerm('');
        setIntegrationSupplierFilter('');
        setExpandedSuppliers({});
        setIsIntegrationModalOpen(true);
    };

    const handleSaveIntegrations = async () => {
        if (!selectedCompanyForIntegrations) return;
        try {
            await api.put(`/companies/${selectedCompanyForIntegrations.id}/integrations`, {
                integration_ids: selectedIntegrationIds
            });
            toast.success('Integrações associadas com sucesso');
            setIsIntegrationModalOpen(false);
            fetchCompanies();
        } catch (error) {
            toast.error('Erro ao salvar integrações');
        } finally {
        }
    };

    const openTokenModal = (company) => {
        if (!company.integrations || company.integrations.length === 0) {
            toast.warning('Associe integrações antes de gerar tokens.');
            return;
        }
        setSelectedCompanyForTokens(company);
        setNewToken(null);
        fetchTokens(company.id);
    };

    const fetchTokens = async (companyId) => {
        try {
            const response = await api.get(`/api-tokens/company/${companyId}`, {
                params: { page: 1, page_size: 100 }
            });
            setApiTokens(response.data.items || []);
            setIsTokenModalOpen(true);
        } catch (error) {
            toast.error('Erro ao carregar tokens');
        } finally {
        }
    };

    const generateNewToken = async () => {
        if (!selectedCompanyForTokens) return;
        try {
            const response = await api.post(`/api-tokens/company/${selectedCompanyForTokens.id}`, {
                token_name: `Token ${new Date().toLocaleDateString('pt-BR')}`
            });
            setNewToken(response.data.token);
            toast.success('Token gerado com sucesso!');
            fetchTokens(selectedCompanyForTokens.id);
            // Update company list to reflect new token count
            fetchCompanies();
        } catch (error) {
            toast.error('Erro ao gerar token');
        } finally {
        }
    };

    const deleteToken = async (tokenId) => {
        toast.confirm(
            'Tem certeza que deseja deletar este token?',
            async () => {
                try {
                    await api.delete(`/api-tokens/${tokenId}`);
                    toast.success('Token deletado com sucesso');
                    if (selectedCompanyForTokens) {
                        fetchTokens(selectedCompanyForTokens.id);
                        // Update company list to reflect new token count
                        fetchCompanies();
                    }
                } catch (error) {
                    toast.error('Erro ao deletar token');
                } finally {
                }
            },
            () => {
                // Cancel - do nothing
            }
        );
    };

    const copyTokenToClipboard = (tokenText) => {
        navigator.clipboard.writeText(tokenText).then(() => {
            toast.success('Token copiado para área de transferência');
        }).catch(() => {
            toast.error('Erro ao copiar token');
        });
    };

    const copyCompanyIdToClipboard = (companyId) => {
        navigator.clipboard.writeText(companyId).then(() => {
            if (toast && toast.success) {
                toast.success('ID da empresa copiado para área de transferência');
            }
        }).catch(() => {
            if (toast && toast.error) {
                toast.error('Erro ao copiar ID da empresa');
            }
        });
    };

    const copyIntegrationIdToClipboard = (integrationId) => {
        navigator.clipboard.writeText(integrationId).then(() => {
            if (toast && toast.success) {
                toast.success('ID da integração copiado para área de transferência');
            }
        }).catch(() => {
            if (toast && toast.error) {
                toast.error('Erro ao copiar ID da integração');
            }
        });
    };

    const copyNewestToken = () => {
        // Pega o primeiro token da lista (mais recente)
        if (apiTokens.length > 0) {
            const newestToken = [...apiTokens].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
            if (newestToken.token) {
                copyTokenToClipboard(newestToken.token);
            } else {
                toast.warning('Token não disponível para copiar');
            }
        }
    };

    const validateToken = async () => {
        if (!tokenValidationInput.trim()) {
            toast.warning('Insira um token para validar');
            return;
        }

        setIsValidatingToken(true);
        try {
            const response = await api.post('/api-tokens/validate', {
                token: tokenValidationInput
            });
            setTokenValidationResult(response.data);
            if (response.data.valid) {
                toast.success('Token validado com sucesso!');
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error('Erro ao validar token');
        } finally {
            setIsValidatingToken(false);
        }
    };

    // Users management functions
    const openUsersModal = async (company) => {
        setSelectedCompanyForUsers(company);
        setUsersLoading(true);
        try {
            const response = await api.get(`/company-users/${company.id}`);
            setCompanyUsers(response.data || []);
        } catch (error) {
            toast.error('Erro ao carregar usuários');
        } finally {
            setUsersLoading(false);
            setIsUsersModalOpen(true);
            setUserSearchTerm('');
        }
    };

    const handleResetPassword = async (userId) => {
        try {
            await api.post(`/company-users/${selectedCompanyForUsers.id}/${userId}/reset-password`);
            toast.success('Email de reset enviado com sucesso!');
        } catch (error) {
            const errorMessage = error.response?.data?.detail || 'Erro ao enviar email de reset';
            toast.error(errorMessage);
        }
    };

    const handleBlockUser = async (userId) => {
        try {
            await api.patch(`/company-users/${selectedCompanyForUsers.id}/${userId}/block`, null, {
                params: { force: true }
            });
            toast.success('Usuário atualizado com sucesso!');
            // Recarregar usuários
            const refreshResponse = await api.get(`/company-users/${selectedCompanyForUsers.id}`);
            setCompanyUsers(refreshResponse.data || []);
        } catch (error) {
            const errorMessage = error.response?.data?.detail || 'Erro ao atualizar usuário';
            toast.error(errorMessage);
        }
    };

    const openDbModal = async (company) => {
        setSelectedCompanyForDb(company);
        setDbStatus(null);
        setIsDbModalOpen(true);
        setDbLoading(true);
        try {
            const response = await api.get(`/companies/${company.id}/database`);
            setDbStatus(response.data);
        } catch (error) {
            toast.error('Erro ao carregar status do banco de dados');
        } finally {
            setDbLoading(false);
        }
    };

    const openConfigsModal = (company) => {
        setSelectedCompanyForConfigs(company);
        setIsConfigsModalOpen(true);
    };

    const handleInitDb = async () => {
        if (!selectedCompanyForDb) return;
        setDbInitializing(true);
        try {
            if (dbStatus.file_exists) {
                // Restart activation - delete the activation record
                await api.delete(`/companies/${selectedCompanyForDb.id}/database/restart`);
                toast.success('Ativação reiniciada com sucesso!');
                // Refresh status to show as pending
                const response = await api.get(`/companies/${selectedCompanyForDb.id}/database`);
                setDbStatus(response.data);
                // Refresh activation status in table
                fetchCompanies();
            } else {
                // Initialize activation
                const response = await api.post(`/companies/${selectedCompanyForDb.id}/database/init`);
                setDbStatus(response.data);
                toast.success('Ativação confirmada com sucesso!');
                // Refresh activation status in table
                fetchCompanies();
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao processar ativação');
        } finally {
            setDbInitializing(false);
        }
    };

    const handleOpenCompanyFrontend = () => {
        const companyFrontendUrl = config.companyFrontendUrl;

        if (!companyFrontendUrl) {
            toast.error('URL do painel da empresa não está configurada');
            return;
        }

        try {
            window.open(companyFrontendUrl, '_blank');
        } catch (error) {
            toast.error('Erro ao abrir painel da empresa');
        }
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleInviteEmailChange = (e) => {
        const email = e.target.value;
        setInviteEmail(email);
        setInviteEmailTouched(true);

        if (email && !validateEmail(email)) {
            setInviteEmailError('Email inválido');
        } else {
            setInviteEmailError('');
        }
    };

    const handleSendInvite = async () => {
        if (!inviteEmail.trim()) {
            setInviteEmailError('Email obrigatório');
            setInviteEmailTouched(true);
            return;
        }

        if (!validateEmail(inviteEmail)) {
            setInviteEmailError('Email inválido');
            return;
        }

        if (!selectedCompanyForDb) {
            toast.error('Empresa não selecionada');
            return;
        }

        setInviteSending(true);
        try {
            await api.post(`/company-activation/send-invite/${selectedCompanyForDb.id}`, {
                email: inviteEmail.trim()
            });
            toast.success(`Email de convite enviado para ${inviteEmail}`);
            setIsInviteModalOpen(false);
            setInviteEmail('');
            setInviteEmailError('');
            setInviteEmailTouched(false);
        } catch (error) {
            const errorMsg = error.response?.data?.detail || 'Erro ao enviar email de convite';
            toast.error(errorMsg);
        } finally {
            setInviteSending(false);
        }
    };

    const renderCoreBadge = (company) => company.is_core ? (
        <span
            title={`${company.associated_companies.length} empresa(s) associada(s)`}
            className="inline-flex items-center px-1.5 py-0.5 rounded border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-300 text-[10px] font-semibold uppercase tracking-wide flex-shrink-0"
        >
            NUCLEO
        </span>
    ) : null;

    // Núcleo candidates: active companies that are not the one being edited nor already associated to another núcleo
    // (the currently selected núcleo is always kept so it renders even if it was deactivated meanwhile)
    const coreOptions = companies.filter(c =>
        c.id !== editingCompany?.id && !c.core_id && (c.active || c.id === formData.core_id)
    );
    const editingCompanyAssociatedCount = editingCompany?.associated_companies?.length || 0;
    const filteredAssociatedCompanies = (editingCompany?.associated_companies || []).filter(assoc => {
        const term = associatedSearchTerm.trim().toLowerCase();
        if (!term) return true;
        const digits = term.replace(/\D/g, '');
        return (
            (assoc.nome_fantasia || '').toLowerCase().includes(term) ||
            assoc.razao_social.toLowerCase().includes(term) ||
            (digits.length > 0 && assoc.cnpj.includes(digits))
        );
    });

    const buildingIcon = <span className="material-icons mx-auto mb-4 opacity-50" style={{fontSize: '48px'}}>domain</span>;
    const plusIcon = <span className="material-icons sm">add</span>;
    const editIcon = <span className="material-icons sm">edit</span>;
    const deleteIcon = <span className="material-icons sm">delete</span>;
    const checkIcon = <span className="material-icons sm">check_circle</span>;
    const xIcon = <span className="material-icons sm">cancel</span>;
    const zapIcon = <span className="material-icons sm">bolt</span>;
    const saveIcon = <span className="material-icons sm">save</span>;
    const closeIcon = <span className="material-icons sm">close</span>;
    const keyIcon = <span className="material-icons sm">vpn_key</span>;
    const copyIcon = <span className="material-icons" style={{fontSize: '14px'}}>content_copy</span>;
    const globeIcon = <span className="material-icons sm">public</span>;
    const userIcon = <span className="material-icons sm">person</span>;
    const blockIcon = <span className="material-icons sm">block</span>;
    const lockIcon = <span className="material-icons sm">vpn_key</span>;
    const settingsIcon = <span className="material-icons sm">settings</span>;

    return (
        <div className="flex-1 overflow-auto">
            <div className="p-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                    <div className="flex items-center gap-3 min-w-0">
                        <span className="material-icons text-blue-500 dark:text-blue-400 flex-shrink-0" style={{fontSize: '40px'}}>apartment</span>
                        <div className="min-w-0">
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Empresas</h1>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Gerenciar empresas cadastradas</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button onClick={() => setIsTokenValidationModalOpen(true)} variant="warning"><span className="material-icons sm">vpn_key</span> Validar Token</Button>
                        <Button onClick={openNewModal} variant="primary">{plusIcon} Nova Empresa</Button>
                    </div>
                </div>
                {/* Search Bar */}
                <div className="mb-6">
                    <div className="relative">
                        <span className="material-icons absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" style={{fontSize: '20px'}}>search</span>
                        <input
                            type="text"
                            placeholder="Pesquisar por ID, nome ou CNPJ..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                </div>

                <div className="relative">
                {/* Desktop table — stays mounted even when hidden so we can keep measuring real overflow */}
                <div
                    ref={companiesTableScrollRef}
                    className={`overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg ${
                        showCompaniesTable ? '' : 'invisible absolute inset-0 -z-10'
                    }`}
                >
                    <table className="w-full min-w-[1100px]">
                        <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                            <tr>
                                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">Razão Social</th>
                                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">CNPJ</th>
                                <th className="px-6 py-3 text-center text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">Ativa</th>
                                <th className="px-6 py-3 text-center text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">Ativação</th>
                                <th className="px-6 py-3 text-center text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">Integrações</th>
                                <th className="px-6 py-3 text-center text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">Tokens</th>
                                <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900 dark:text-gray-100"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                            {companies.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                                        <div className="flex flex-col items-center gap-2">
                                            {buildingIcon}
                                            <p>Nenhuma empresa cadastrada</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredCompanies.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                                        <div className="flex flex-col items-center gap-2">
                                            <span className="material-icons text-gray-400 dark:text-gray-500" style={{fontSize: '40px'}}>search_off</span>
                                            <p>Nenhuma empresa encontrada para "{searchTerm}"</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredCompanies.map(company => (
                                    <tr key={company.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition">
                                        <td className="px-6 py-4">
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <p className="text-sm text-gray-900 dark:text-gray-100">{company.razao_social}</p>
                                                    {!company.nome_fantasia && renderCoreBadge(company)}
                                                </div>
                                                {company.nome_fantasia && (
                                                    <div className="flex items-center gap-2 mt-0.5">
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">{company.nome_fantasia}</p>
                                                        {renderCoreBadge(company)}
                                                    </div>
                                                )}
                                                <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full whitespace-nowrap">
                                                    <span className="text-xs font-mono text-gray-600 dark:text-gray-300">{company.id}</span>
                                                    <button
                                                        onClick={() => copyCompanyIdToClipboard(company.id)}
                                                        className="flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition"
                                                        title="Copiar ID da empresa"
                                                    >
                                                        {copyIcon}
                                                    </button>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">{formatCNPJ(company.cnpj)}</td>
                                        <td className="px-6 py-4 text-sm text-center whitespace-nowrap">
                                            {company.active ? (
                                                <span className="text-green-600 dark:text-green-400">{checkIcon}</span>
                                            ) : (
                                                <span className="text-gray-500 dark:text-gray-400">{xIcon}</span>
                                            )}
                                        </td>
                                        <td className="text-center px-6 py-4 text-sm whitespace-nowrap">
                                            {activationStatus[company.id] ? (
                                                <span className="inline-flex items-center px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-xs font-medium">
                                                    <span className="material-icons" style={{fontSize: '14px', marginRight: '4px'}}>check_circle</span>
                                                    Concluída
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-3 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded-full text-xs font-medium">
                                                    <span className="material-icons" style={{fontSize: '14px', marginRight: '4px'}}>schedule</span>
                                                    Pendente
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-center px-6 py-4 text-sm text-blue-600 dark:text-blue-400 whitespace-nowrap">
                                            {company.integrations && company.integrations.length > 0 ? `${company.integrations.length}` : 'Nenhuma'}
                                        </td>
                                        <td className="text-center px-6 py-4 text-sm text-amber-600 dark:text-amber-400 whitespace-nowrap">
                                            {company.api_tokens_count || 0}
                                        </td>
                                        <td className="px-6 py-4 text-right whitespace-nowrap">
                                            <div className="flex gap-1.5 justify-end">
                                                <Button size="xs" variant="primary" onClick={() => openUsersModal(company)} title="Gerenciar Usuários">{userIcon}</Button>
                                                <Button size="xs" variant="primary" onClick={() => handleOpenCompanyFrontend()} title="Acessar Login da Empresa">{globeIcon}</Button>
                                                <Button size="xs" variant="primary" onClick={() => openTokenModal(company)} title="Tokens de API">{keyIcon}</Button>
                                                <Button size="xs" variant="primary" onClick={() => openIntegrationModal(company)}>{zapIcon}</Button>
                                                <Button size="xs" variant="primary" onClick={() => openConfigsModal(company)} title="Configurações da Empresa">{settingsIcon}</Button>
                                                <Button size="xs" variant="primary" onClick={() => openDbModal(company)} title="Ativação da Empresa"><span className="material-icons sm">verified</span></Button>
                                                <Button size="xs" variant="primary" onClick={() => openEditModal(company)}>{editIcon}</Button>
                                                <Button size="xs" variant="danger" onClick={() => handleDelete(company)}>{deleteIcon}</Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Mobile / tablet card list */}
                <div className={`${showCompaniesTable ? 'hidden' : 'block'} space-y-3`}>
                    {companies.length === 0 ? (
                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg py-12 text-center text-gray-500 dark:text-gray-400">
                            <div className="flex flex-col items-center gap-2">
                                {buildingIcon}
                                <p>Nenhuma empresa cadastrada</p>
                            </div>
                        </div>
                    ) : filteredCompanies.length === 0 ? (
                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg py-12 text-center text-gray-500 dark:text-gray-400">
                            <div className="flex flex-col items-center gap-2">
                                <span className="material-icons text-gray-400 dark:text-gray-500" style={{fontSize: '40px'}}>search_off</span>
                                <p>Nenhuma empresa encontrada para "{searchTerm}"</p>
                            </div>
                        </div>
                    ) : (
                        filteredCompanies.map(company => (
                            <div key={company.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800">
                                <div className="flex items-start gap-3 text-xs">
                                    <div className="w-1/2 min-w-0 flex items-start gap-1.5">
                                        <span className={`flex-shrink-0 mt-0.5 ${company.active ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
                                            <span className="material-icons" style={{fontSize: '16px'}}>{company.active ? 'check_circle' : 'cancel'}</span>
                                        </span>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{company.razao_social}</p>
                                                {!company.nome_fantasia && renderCoreBadge(company)}
                                            </div>
                                            {company.nome_fantasia && (
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{company.nome_fantasia}</p>
                                                    {renderCoreBadge(company)}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="w-1/2 grid grid-cols-4 gap-2">
                                        <div className="min-w-0">
                                            <p className="text-gray-400 dark:text-gray-500 truncate">CNPJ</p>
                                            <p className="text-gray-700 dark:text-gray-300 truncate">{formatCNPJ(company.cnpj)}</p>
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-gray-400 dark:text-gray-500 truncate">Ativação</p>
                                            {activationStatus[company.id] ? (
                                                <span className="inline-flex items-center px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-[10px] font-medium truncate">
                                                    <span className="material-icons flex-shrink-0" style={{fontSize: '11px', marginRight: '2px'}}>check_circle</span>
                                                    Concluída
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-1.5 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded-full text-[10px] font-medium truncate">
                                                    <span className="material-icons flex-shrink-0" style={{fontSize: '11px', marginRight: '2px'}}>schedule</span>
                                                    Pendente
                                                </span>
                                            )}
                                        </div>
                                        <div className="min-w-0 text-center">
                                            <p className="text-gray-400 dark:text-gray-500 truncate">Integrações</p>
                                            <span className="inline-flex items-center justify-center min-w-[22px] px-2 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-full text-[11px] font-medium">
                                                {company.integrations && company.integrations.length > 0 ? company.integrations.length : '0'}
                                            </span>
                                        </div>
                                        <div className="min-w-0 text-center">
                                            <p className="text-gray-400 dark:text-gray-500 truncate">Tokens</p>
                                            <span className="inline-flex items-center justify-center min-w-[22px] px-2 py-0.5 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 rounded-full text-[11px] font-medium">
                                                {company.api_tokens_count || 0}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full max-w-full">
                                    <span className="text-[10px] font-mono text-gray-600 dark:text-gray-300 truncate">{company.id}</span>
                                    <button
                                        onClick={() => copyCompanyIdToClipboard(company.id)}
                                        className="flex-shrink-0 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition"
                                        title="Copiar ID da empresa"
                                    >
                                        {copyIcon}
                                    </button>
                                </div>

                                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 relative" data-actions-menu>
                                    <button
                                        type="button"
                                        onClick={() => setOpenActionsMenuId(openActionsMenuId === company.id ? null : company.id)}
                                        className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-500 transition"
                                    >
                                        <span className="flex items-center gap-2">
                                            <span className="material-icons sm">more_horiz</span>
                                            Ações
                                        </span>
                                        <span className="material-icons sm text-gray-400 dark:text-gray-500">
                                            {openActionsMenuId === company.id ? 'expand_less' : 'expand_more'}
                                        </span>
                                    </button>

                                    {openActionsMenuId === company.id && (
                                        <div className="absolute left-0 right-0 mt-1 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden">
                                            {[
                                                { icon: userIcon, label: 'Gerenciar Usuários', onClick: () => openUsersModal(company) },
                                                { icon: globeIcon, label: 'Acessar Login da Empresa', onClick: () => handleOpenCompanyFrontend() },
                                                { icon: keyIcon, label: 'Tokens de API', onClick: () => openTokenModal(company) },
                                                { icon: zapIcon, label: 'Integrações', onClick: () => openIntegrationModal(company) },
                                                { icon: settingsIcon, label: 'Configurações da Empresa', onClick: () => openConfigsModal(company) },
                                                { icon: <span className="material-icons sm">verified</span>, label: 'Ativação da Empresa', onClick: () => openDbModal(company) },
                                                { icon: editIcon, label: 'Editar', onClick: () => openEditModal(company) },
                                                { icon: deleteIcon, label: 'Excluir', onClick: () => handleDelete(company), danger: true },
                                            ].map((action, idx) => (
                                                <button
                                                    key={idx}
                                                    type="button"
                                                    onClick={() => { action.onClick(); setOpenActionsMenuId(null); }}
                                                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition ${
                                                        action.danger
                                                            ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                                                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60'
                                                    }`}
                                                >
                                                    {action.icon}
                                                    {action.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
                </div>
            </div>

            <Modal isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setFormErrors({}); }} title={editingCompany ? 'Editar Empresa' : 'Nova Empresa'}>
                <form onSubmit={handleSubmit} className="space-y-4">
                    {editingCompany && (
                        <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">ID da Empresa</p>
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full">
                                <span className="text-xs font-mono text-gray-600 dark:text-gray-300">{editingCompany.id}</span>
                                <button
                                    type="button"
                                    onClick={() => copyCompanyIdToClipboard(editingCompany.id)}
                                    className="flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition"
                                    title="Copiar ID da empresa"
                                >
                                    {copyIcon}
                                </button>
                            </div>
                        </div>
                    )}
                    <Input label="CNPJ" placeholder="00.000.000/0000-00" value={formatCNPJ(formData.cnpj)} onChange={handleCNPJChange} error={formErrors.cnpj} />
                    <Input label="Razão Social" placeholder="Nome da empresa" value={formData.razao_social} onChange={(e) => setFormData({ ...formData, razao_social: e.target.value })} error={formErrors.razao_social} />
                    <Input label="Nome Fantasia" placeholder="Nome fantasia (opcional)" value={formData.nome_fantasia} onChange={(e) => setFormData({ ...formData, nome_fantasia: e.target.value })} />
                    {/* A núcleo can't belong to another núcleo, so its own select is replaced by the list of its associated companies */}
                    {editingCompanyAssociatedCount === 0 && (
                        <CompanySelect
                            label="Núcleo"
                            helperText="Núcleo ao qual esta empresa está associada. Deixe vazio para não associar."
                            value={formData.core_id}
                            onChange={(id) => setFormData({ ...formData, core_id: id })}
                            options={coreOptions}
                        />
                    )}
                    {editingCompanyAssociatedCount > 0 && (
                        // The whole section is the scroll container; its header (title + search) stays pinned.
                        <section className="border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 max-h-80 overflow-y-auto px-4 pb-4">
                            <div className="sticky top-0 z-10 -mx-4 px-4 pt-4 pb-3 bg-gray-50 dark:bg-gray-700">
                                <div className="flex items-center gap-2 mb-3">
                                    <h3 className="text-sm font-medium">Empresas associadas</h3>
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-300 text-[10px] font-semibold">
                                        {associatedSearchTerm.trim() ? `${filteredAssociatedCompanies.length} de ${editingCompanyAssociatedCount}` : editingCompanyAssociatedCount}
                                    </span>
                                </div>
                                <div className="relative">
                                    <span className="material-icons absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none" style={{fontSize: '18px'}}>search</span>
                                    <input
                                        type="text"
                                        value={associatedSearchTerm}
                                        onChange={(e) => setAssociatedSearchTerm(e.target.value)}
                                        placeholder="Filtrar por nome ou CNPJ..."
                                        className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>
                            {filteredAssociatedCompanies.length === 0 ? (
                                <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">Nenhuma empresa encontrada</p>
                            ) : (
                                <ul className="space-y-2">
                                    {filteredAssociatedCompanies.map(assoc => (
                                        <li
                                            key={assoc.id}
                                            className={`flex items-center gap-3 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md ${assoc.active ? '' : 'opacity-60'}`}
                                        >
                                            <span className="material-icons text-gray-400 dark:text-gray-500 flex-shrink-0" style={{fontSize: '20px'}}>apartment</span>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <p className="text-sm text-gray-900 dark:text-gray-100 truncate">{assoc.nome_fantasia || assoc.razao_social}</p>
                                                    {!assoc.active && (
                                                        <span className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 flex-shrink-0">inativa</span>
                                                    )}
                                                </div>
                                                {assoc.nome_fantasia && (
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{assoc.razao_social}</p>
                                                )}
                                            </div>
                                            <div className="flex flex-col items-end gap-1 min-w-0 max-w-[60%] flex-shrink-0">
                                                <span className="text-xs font-mono text-gray-500 dark:text-gray-400">{formatCNPJ(assoc.cnpj)}</span>
                                                <div className="inline-flex items-center gap-1.5 max-w-full px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-full">
                                                    <span className="text-[11px] font-mono text-gray-600 dark:text-gray-300 truncate">{assoc.id}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => copyCompanyIdToClipboard(assoc.id)}
                                                        className="flex items-center justify-center flex-shrink-0 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition"
                                                        title="Copiar ID da empresa"
                                                    >
                                                        {copyIcon}
                                                    </button>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>
                    )}
                    <label className="flex items-center gap-2">
                        <input type="checkbox" checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} className="w-4 h-4 rounded" />
                        <span className="text-sm">Ativa</span>
                    </label>
                    <div className="flex gap-2 pt-4">
                        <Button type="submit" variant="primary" className="flex-1">{editingCompany ? editIcon : plusIcon} {editingCompany ? 'Atualizar' : 'Criar'}</Button>
                        <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)} className="flex-1">{closeIcon} Cancelar</Button>
                    </div>
                </form>
            </Modal>

            <Modal
                isOpen={!!coreDeleteTarget}
                onClose={closeCoreDeleteModal}
                loading={coreDeleteProcessing}
                title={`Excluir núcleo - ${coreDeleteTarget?.nome_fantasia || coreDeleteTarget?.razao_social || ''}`}
            >
                {coreDeleteTarget && (() => {
                    const associated = coreDeleteTarget.associated_companies || [];
                    const allSelected = associated.length > 0 && coreDeleteSelectedIds.length === associated.length;
                    return (
                        <div className="space-y-4">
                            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                                <p className="text-xs text-amber-700 dark:text-amber-300">
                                    Este núcleo possui {associated.length} empresa{associated.length !== 1 ? 's' : ''} associada{associated.length !== 1 ? 's' : ''}.
                                    Para excluí-lo, é necessário desassociar todas as empresas abaixo.
                                </p>
                            </div>

                            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                <label className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 cursor-pointer border-b border-gray-200 dark:border-gray-700">
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        onChange={toggleAllCoreDelete}
                                        disabled={coreDeleteProcessing}
                                        className="w-5 h-5 rounded border-gray-300 text-blue-500 cursor-pointer flex-shrink-0"
                                    />
                                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Marcar todas</span>
                                    <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">{coreDeleteSelectedIds.length} de {associated.length}</span>
                                </label>
                                <div className="max-h-[28rem] overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700">
                                    {associated.map(assoc => (
                                        <label key={assoc.id} className="flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={coreDeleteSelectedIds.includes(assoc.id)}
                                                onChange={() => toggleCoreDeleteId(assoc.id)}
                                                disabled={coreDeleteProcessing}
                                                className="w-5 h-5 rounded border-gray-300 text-blue-500 cursor-pointer flex-shrink-0"
                                            />
                                            <span className="material-icons text-gray-400 dark:text-gray-500 flex-shrink-0" style={{fontSize: '20px'}}>apartment</span>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm text-gray-900 dark:text-gray-100 truncate">{assoc.nome_fantasia || assoc.razao_social}</p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                    {assoc.nome_fantasia ? `${assoc.razao_social} · ` : ''}{formatCNPJ(assoc.cnpj)}
                                                </p>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {!allSelected && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Marque todas as empresas para poder excluir o núcleo, ou desassocie apenas as selecionadas e exclua depois.
                                </p>
                            )}

                            <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                                <Button
                                    variant="danger"
                                    onClick={handleDisassociateAllAndDelete}
                                    disabled={!allSelected || coreDeleteProcessing}
                                    className="flex-1"
                                >
                                    {deleteIcon} Desassociar e excluir
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={handleDisassociateSelected}
                                    disabled={coreDeleteSelectedIds.length === 0 || coreDeleteProcessing}
                                    className="flex-1"
                                >
                                    Desassociar selecionadas
                                </Button>
                                <Button variant="secondary" onClick={closeCoreDeleteModal} disabled={coreDeleteProcessing} className="flex-1">
                                    {closeIcon} Cancelar
                                </Button>
                            </div>
                        </div>
                    );
                })()}
            </Modal>

            <Modal isOpen={isIntegrationModalOpen} onClose={() => setIsIntegrationModalOpen(false)} title={`Associar Integrações - ${selectedCompanyForIntegrations?.razao_social || ''}`}>
                <div className="space-y-4">
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                        <p className="text-xs text-blue-700 dark:text-blue-300">
                            Selecione as integrações que deseja associar a esta empresa. Você poderá gerar tokens de API específicos para essas integrações.
                        </p>
                    </div>

                    {integrations.length === 0 ? (
                        <div className="text-center py-8">
                            <p className="text-gray-600 dark:text-gray-400 text-sm">Nenhuma integração disponível</p>
                        </div>
                    ) : (() => {
                        const supplierOptions = [...new Map(
                            integrations.filter(i => i.supplier).map(i => [i.supplier.id, i.supplier])
                        ).values()].sort((a, b) => a.name.localeCompare(b.name));

                        const filteredModalIntegrations = integrations.filter(integration => {
                            const matchesSearch = !integrationSearchTerm ||
                                integration.name.toLowerCase().includes(integrationSearchTerm.toLowerCase()) ||
                                (integration.supplier?.name || '').toLowerCase().includes(integrationSearchTerm.toLowerCase()) ||
                                (integration.system?.description || '').toLowerCase().includes(integrationSearchTerm.toLowerCase()) ||
                                (integration.system?.system_type?.name || '').toLowerCase().includes(integrationSearchTerm.toLowerCase());
                            const matchesSupplier = !integrationSupplierFilter || integration.supplier?.id === integrationSupplierFilter;
                            return matchesSearch && matchesSupplier;
                        });

                        return (
                            <div>
                                <div className="flex gap-2 mb-3">
                                    <input
                                        type="text"
                                        placeholder="Filtrar integrações..."
                                        value={integrationSearchTerm}
                                        onChange={(e) => setIntegrationSearchTerm(e.target.value)}
                                        className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <select
                                        value={integrationSupplierFilter}
                                        onChange={(e) => setIntegrationSupplierFilter(e.target.value)}
                                        className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">Todos os fornecedores</option>
                                        {supplierOptions.map(supplier => (
                                            <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                                    Integrações Disponíveis ({filteredModalIntegrations.length})
                                </h3>
                                <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-[32rem] overflow-y-auto">
                                    {filteredModalIntegrations.length === 0 ? (
                                        <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
                                            Nenhuma integração encontrada
                                        </div>
                                    ) : (
                                        (() => {
                                            const groupedIntegrations = groupIntegrationsBySupplier(filteredModalIntegrations);
                                            const supplierKeys = Object.keys(groupedIntegrations).sort((a, b) => {
                                                const nameA = groupedIntegrations[a].name.toLowerCase();
                                                const nameB = groupedIntegrations[b].name.toLowerCase();
                                                return nameA.localeCompare(nameB);
                                            });

                                            return (
                                                <>
                                                    {supplierKeys.map((supplierId) => {
                                                        const supplier = groupedIntegrations[supplierId];
                                                        const isExpanded = expandedSuppliers[supplierId];
                                                        const allSelected = supplier.integrations.every(i => selectedIntegrationIds.includes(i.id));
                                                        const someSelected = supplier.integrations.some(i => selectedIntegrationIds.includes(i.id));

                                                        return (
                                                            <div key={supplierId} className="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                                                                {/* Acordeão Header */}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => toggleSupplierExpansion(supplierId)}
                                                                    className="w-full flex items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition text-left"
                                                                >
                                                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={allSelected}
                                                                            indeterminate={someSelected && !allSelected}
                                                                            onChange={(e) => toggleSupplierIntegrations(supplierId, supplier.integrations, e.target.checked)}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            className="w-5 h-5 rounded border-gray-300 text-blue-500 cursor-pointer flex-shrink-0"
                                                                        />
                                                                        <span className="material-icons text-gray-400 dark:text-gray-500 transition flex-shrink-0" style={{fontSize: '18px'}}>
                                                                            {isExpanded ? 'expand_less' : 'expand_more'}
                                                                        </span>
                                                                        <div className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded flex items-center justify-center flex-shrink-0">
                                                                            {supplier.logo_url ? (
                                                                                <img src={supplier.logo_url} alt={supplier.name} className="w-full h-full object-contain p-0.5 rounded" />
                                                                            ) : (
                                                                                <span className="material-icons text-gray-400 dark:text-gray-500" style={{fontSize: '14px'}}>image</span>
                                                                            )}
                                                                        </div>
                                                                        <p className="font-semibold text-sm text-gray-900 dark:text-gray-100">{supplier.name}</p>
                                                                    </div>
                                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 flex-shrink-0">
                                                                        {supplier.integrations.length}
                                                                    </span>
                                                                </button>

                                                                {/* Conteúdo expandido */}
                                                                {isExpanded && (
                                                                    <div className="bg-white dark:bg-gray-800/50 divide-y divide-gray-200 dark:divide-gray-700">
                                                                        {supplier.integrations.map((integration) => (
                                                                            <label
                                                                                key={integration.id}
                                                                                className="flex items-start gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition cursor-pointer"
                                                                            >
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={selectedIntegrationIds.includes(integration.id)}
                                                                                    onChange={(e) => {
                                                                                        if (e.target.checked) {
                                                                                            setSelectedIntegrationIds([...selectedIntegrationIds, integration.id]);
                                                                                        } else {
                                                                                            setSelectedIntegrationIds(selectedIntegrationIds.filter(id => id !== integration.id));
                                                                                        }
                                                                                    }}
                                                                                    className="w-5 h-5 rounded border-gray-300 text-blue-500 cursor-pointer flex-shrink-0 mt-0.5"
                                                                                />
                                                                                <div className="flex-1 min-w-0">
                                                                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                                                                                        {integration.name}
                                                                                    </p>
                                                                                    <div className="flex flex-wrap gap-1">
                                                                                        {integration.system && (
                                                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                                                                                                {integration.system.description}
                                                                                            </span>
                                                                                        )}
                                                                                        {integration.system?.system_type && (
                                                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300">
                                                                                                {integration.system.system_type.name}
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            </label>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </>
                                            );
                                        })()
                                    )}
                                </div>
                            </div>
                        );
                    })()}

                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                            {selectedIntegrationIds.length} integração{selectedIntegrationIds.length !== 1 ? 's' : ''} selecionada{selectedIntegrationIds.length !== 1 ? 's' : ''}
                        </p>
                        <div className="flex gap-2">
                            <Button variant="primary" onClick={handleSaveIntegrations} className="flex-1">{saveIcon} Salvar</Button>
                            <Button variant="secondary" onClick={() => setIsIntegrationModalOpen(false)} className="flex-1">{closeIcon} Cancelar</Button>
                        </div>
                    </div>
                </div>
            </Modal>

            <Modal isOpen={isTokenModalOpen} onClose={() => { setIsTokenModalOpen(false); setNewToken(null); }} title={`Tokens de API - ${selectedCompanyForTokens?.razao_social || ''}`}>
                <div className="space-y-4">
                    {newToken && (
                        <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                            <p className="text-sm font-medium text-green-900 dark:text-green-100 mb-2">Token Gerado com Sucesso!</p>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    readOnly
                                    value={newToken}
                                    className="flex-1 px-2 py-1 text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded font-mono text-gray-600 dark:text-gray-300"
                                />
                                <Button
                                    size="sm"
                                    variant="primary"
                                    onClick={() => copyTokenToClipboard(newToken)}
                                    className="whitespace-nowrap"
                                >
                                    {copyIcon}
                                </Button>
                            </div>
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">Aqui está seu novo token de acesso</p>
                        </div>
                    )}

                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Tokens Existentes</h3>
                        {apiTokens.length === 0 ? (
                            <p className="text-gray-600 dark:text-gray-400 text-sm">Nenhum token gerado ainda</p>
                        ) : (
                            <div className="space-y-2">
                                {[...apiTokens].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((tokenItem, index) => {
                                    const createdDate = new Date(tokenItem.created_at);
                                    const localDate = new Date(createdDate.getTime() - (createdDate.getTimezoneOffset() * 60000));
                                    const isNewest = index === 0;

                                    return (
                                    <div key={tokenItem.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                                {tokenItem.token_name || 'Token sem nome'} <span className="text-xs text-gray-500">({tokenItem.id.substring(0, 8)}...)</span>
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                {localDate.toLocaleDateString('pt-BR')} às {localDate.toLocaleTimeString('pt-BR')}
                                            </p>
                                        </div>
                                        <div className="flex gap-2 ml-3">
                                            <Button
                                                size="sm"
                                                variant="primary"
                                                onClick={copyNewestToken}
                                                disabled={!isNewest}
                                                title="Copiar token mais recente"
                                            >
                                                {copyIcon}
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="danger"
                                                onClick={() => deleteToken(tokenItem.id)}
                                               
                                                title="Deletar token"
                                            >
                                                {deleteIcon}
                                            </Button>
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Button
                            variant="primary"
                           
                            onClick={generateNewToken}
                            className="flex-1"
                        >
                            {plusIcon} Novo Token
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={() => { setIsTokenModalOpen(false); setNewToken(null); }}
                           
                            className="flex-1"
                        >
                            {closeIcon} Fechar
                        </Button>
                    </div>
                </div>
            </Modal>

            <Modal isOpen={isTokenValidationModalOpen} onClose={() => { setIsTokenValidationModalOpen(false); setTokenValidationInput(''); setTokenValidationResult(null); }} title="Validar Token de API" loading={isValidatingToken}>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Cole seu Token de API</label>
                        <textarea
                            value={tokenValidationInput}
                            onChange={(e) => setTokenValidationInput(e.target.value)}
                            placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-xs resize-none"
                            rows={4}
                            disabled={isValidatingToken}
                        />
                    </div>

                    {tokenValidationResult && (
                        <div className={`p-4 rounded-lg border-l-4 ${tokenValidationResult.valid ? 'bg-green-50 dark:bg-green-900/20 border-green-400 dark:border-green-600' : 'bg-red-50 dark:bg-red-900/20 border-red-400 dark:border-red-600'}`}>
                            <div className="flex items-start gap-3">
                                <span className={`material-icons text-lg ${tokenValidationResult.valid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                    {tokenValidationResult.valid ? 'check_circle' : 'error'}
                                </span>
                                <div className="flex-1">
                                    <p className={`font-semibold ${tokenValidationResult.valid ? 'text-green-900 dark:text-green-100' : 'text-red-900 dark:text-red-100'}`}>
                                        {tokenValidationResult.message}
                                    </p>

                                    {tokenValidationResult.valid && (
                                        <div className="mt-3 space-y-2">
                                            <div className="text-sm">
                                                <p className="font-medium text-gray-700 dark:text-gray-300">ID da Empresa:</p>
                                                <p className="text-gray-600 dark:text-gray-400 font-mono text-xs mt-1">{tokenValidationResult.company_id}</p>
                                            </div>

                                            <div className="text-sm">
                                                <p className="font-medium text-gray-700 dark:text-gray-300">Empresa:</p>
                                                <p className="text-gray-600 dark:text-gray-400 mt-1">{tokenValidationResult.company_name}</p>
                                            </div>

                                            {tokenValidationResult.integration_names.length > 0 && (
                                                <div className="text-sm">
                                                    <p className="font-medium text-gray-700 dark:text-gray-300">Integrações Associadas:</p>
                                                    <ul className="mt-2 space-y-1">
                                                        {tokenValidationResult.integration_names.map((name, idx) => (
                                                            <li key={idx} className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                                                <span className="material-icons text-xs text-blue-500">check</span>
                                                                {name}
                                                                <span className="text-xs text-gray-500">({tokenValidationResult.integration_ids[idx]})</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                            {(tokenValidationResult.associated_company_ids || []).length > 0 && (
                                                <div className="text-sm">
                                                    <p className="font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                                        Empresas Associadas (Núcleo):
                                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-300 text-[10px] font-semibold uppercase tracking-wide">
                                                            NUCLEO
                                                        </span>
                                                    </p>
                                                    <ul className="mt-2 space-y-1">
                                                        {tokenValidationResult.associated_company_ids.map((id) => {
                                                            const assoc = (tokenValidationResult.associated_companies || []).find(c => c.id === id);
                                                            return (
                                                                <li key={id} className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                                                    <span className="material-icons text-xs text-purple-500">apartment</span>
                                                                    {assoc ? (assoc.nome_fantasia || assoc.razao_social) : <span className="italic">Empresa não encontrada</span>}
                                                                    <span className="text-xs text-gray-500">({id})</span>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Button
                            variant="primary"
                            loading={isValidatingToken}
                            onClick={validateToken}
                            className="flex-1"
                        >
                            <span className="material-icons sm">check</span> Validar
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={() => { setIsTokenValidationModalOpen(false); setTokenValidationInput(''); setTokenValidationResult(null); }}
                            disabled={isValidatingToken}
                            className="flex-1"
                        >
                            <span className="material-icons sm">close</span> Fechar
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Users Management Modal */}
            <Modal isOpen={isUsersModalOpen} onClose={() => { setIsUsersModalOpen(false); setUserSearchTerm(''); }} title={`Gerenciar Usuários - ${selectedCompanyForUsers?.razao_social || ''}`} loading={usersLoading}>
                <div className="space-y-4">
                    {companyUsers.length > 0 && (
                        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-6 py-4">
                            <div className="flex items-center gap-3">
                                <span className="material-icons text-gray-400">search</span>
                                <input
                                    type="text"
                                    placeholder="Pesquisar por usuário ou email..."
                                    value={userSearchTerm}
                                    onChange={(e) => setUserSearchTerm(e.target.value)}
                                    className="flex-1 bg-transparent text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none text-sm"
                                />
                                {userSearchTerm && (
                                    <button
                                        onClick={() => setUserSearchTerm('')}
                                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
                                    >
                                        <span className="material-icons sm">close</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {usersLoading ? (
                        <div className="flex items-center justify-center h-64">
                            <div className="text-center">
                                <span className="material-icons text-4xl text-blue-500 animate-spin">refresh</span>
                                <p className="text-gray-600 dark:text-gray-400 mt-2">Carregando usuários...</p>
                            </div>
                        </div>
                    ) : companyUsers.filter(user =>
                        user.username.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                        user.email.toLowerCase().includes(userSearchTerm.toLowerCase())
                    ).length === 0 ? (
                        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                            <span className="material-icons text-4xl text-gray-300 block mb-2">people</span>
                            <p className="text-gray-500 dark:text-gray-400">
                                {userSearchTerm ? 'Nenhum usuário encontrado' : 'Nenhum usuário cadastrado ainda'}
                            </p>
                        </div>
                    ) : (
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
                            <table className="w-full">
                                <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Usuário</th>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Email</th>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Status</th>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Papel</th>
                                        <th className="px-6 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {companyUsers.filter(user =>
                                        user.username.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                                        user.email.toLowerCase().includes(userSearchTerm.toLowerCase())
                                    ).map((user) => (
                                        <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition border-b border-gray-200 dark:border-gray-600">
                                            <td className="px-6 py-4 text-sm text-gray-800 dark:text-gray-200">{user.username}</td>
                                            <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{user.email}</td>
                                            <td className="px-6 py-4">
                                                <span className={`badge ${user.is_active ? 'active' : 'inactive'}`}>
                                                    {user.is_active ? 'Ativo' : 'Bloqueado'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                {user.is_admin && <span className="badge admin">Administrador</span>}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex gap-2 justify-center">
                                                    <button
                                                        onClick={() => handleResetPassword(user.id)}
                                                        className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition"
                                                        title="Enviar link de reset de senha"
                                                    >
                                                        <span className="material-icons sm">vpn_key</span>
                                                    </button>
                                                    <button
                                                        onClick={() => handleBlockUser(user.id)}
                                                        className={`p-1.5 rounded-lg transition ${
                                                            user.is_active
                                                                ? 'text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30'
                                                                : 'text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30'
                                                        }`}
                                                        title={user.is_active ? 'Bloquear usuário' : 'Desbloquear usuário'}
                                                    >
                                                        <span className="material-icons sm">{user.is_active ? 'block' : 'check_circle'}</span>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </Modal>

            {/* Company Configs Modal */}
            <CompanyConfigsModal
                isOpen={isConfigsModalOpen}
                onClose={() => setIsConfigsModalOpen(false)}
                company={selectedCompanyForConfigs}
            />

            {/* Company Database Modal */}
            <Modal
                isOpen={isDbModalOpen}
                onClose={() => { setIsDbModalOpen(false); setDbStatus(null); }}
                title={`Ativação - ${selectedCompanyForDb?.razao_social || ''}`}
                loading={dbLoading}
            >
                <div className="space-y-4">
                    {dbLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="text-center">
                                <span className="material-icons text-4xl text-blue-500 animate-spin block mb-2">refresh</span>
                                <p className="text-gray-500 dark:text-gray-400 text-sm">Verificando status de ativação...</p>
                            </div>
                        </div>
                    ) : dbStatus ? (
                        <>
                            {/* Status card */}
                            <div className={`rounded-lg border p-4 flex items-start gap-3 ${
                                dbStatus.file_exists
                                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                    : dbStatus.has_record
                                        ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                                        : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                            }`}>
                                <span className={`material-icons text-xl mt-0.5 ${
                                    dbStatus.file_exists ? 'text-green-600 dark:text-green-400'
                                        : dbStatus.has_record ? 'text-yellow-600 dark:text-yellow-400'
                                        : 'text-red-600 dark:text-red-400'
                                }`}>
                                    {dbStatus.file_exists ? 'check_circle' : dbStatus.has_record ? 'warning' : 'error'}
                                </span>
                                <div className="flex-1">
                                    <p className={`font-semibold text-sm ${
                                        dbStatus.file_exists ? 'text-green-800 dark:text-green-200'
                                            : dbStatus.has_record ? 'text-yellow-800 dark:text-yellow-200'
                                            : 'text-red-800 dark:text-red-200'
                                    }`}>
                                        {dbStatus.file_exists ? 'Ativação concluída'
                                            : dbStatus.has_record ? 'Ativação pendente de confirmação'
                                            : 'Ativação não iniciada'}
                                    </p>
                                    <p className={`text-xs mt-1 ${
                                        dbStatus.file_exists ? 'text-green-700 dark:text-green-300'
                                            : dbStatus.has_record ? 'text-yellow-700 dark:text-yellow-300'
                                            : 'text-red-700 dark:text-red-300'
                                    }`}>
                                        {dbStatus.file_exists
                                            ? 'A empresa está pronta para usar a plataforma.'
                                            : 'Clique em "Confirmar Ativação" para finalizar.'}
                                    </p>
                                    {!dbStatus.file_exists && (
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => setIsInviteModalOpen(true)}
                                            className="mt-3"
                                            title="Enviar email de convite para ativação"
                                        >
                                            <span className="material-icons sm">email</span>
                                            Enviar Convite
                                        </Button>
                                    )}
                                </div>
                            </div>

                            {/* Details */}
                            <div className="space-y-2 text-sm">
                                {dbStatus.has_record && (
                                    <>
                                        {dbStatus.admin_email && (
                                            <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                                                <span className="text-gray-500 dark:text-gray-400">Email do Admin</span>
                                                <span className="text-gray-700 dark:text-gray-300">{dbStatus.admin_email}</span>
                                            </div>
                                        )}
                                        {dbStatus.admin_confirmed_at && (
                                            <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                                                <span className="text-gray-500 dark:text-gray-400">Código Confirmado em</span>
                                                <span className="text-gray-700 dark:text-gray-300">
                                                    {new Date(dbStatus.admin_confirmed_at).toLocaleString('pt-BR')}
                                                </span>
                                            </div>
                                        )}
                                        {dbStatus.lgpd_accepted_at && (
                                            <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                                                <span className="text-gray-500 dark:text-gray-400">LGPD Aceito em</span>
                                                <span className="text-gray-700 dark:text-gray-300">
                                                    {new Date(dbStatus.lgpd_accepted_at).toLocaleString('pt-BR')}
                                                </span>
                                            </div>
                                        )}
                                        {dbStatus.user_agent && (
                                            <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                                                <span className="text-gray-500 dark:text-gray-400">Navegador</span>
                                                <span className="text-xs text-gray-700 dark:text-gray-300 max-w-xs text-right truncate">{dbStatus.user_agent}</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between py-2">
                                            <span className="text-gray-500 dark:text-gray-400">ID da Ativação</span>
                                            <code className="text-xs text-gray-700 dark:text-gray-300 font-mono">{dbStatus.database_hash}</code>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="flex gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                                <Button
                                    variant={dbStatus.file_exists ? 'secondary' : 'primary'}
                                    onClick={handleInitDb}
                                    loading={dbInitializing}
                                    disabled={dbInitializing}
                                    className="flex-1"
                                >
                                    <span className="material-icons sm">{dbStatus.file_exists ? 'refresh' : 'verified'}</span>
                                    {dbInitializing ? 'Processando...' : dbStatus.file_exists ? 'Reiniciar Ativação' : 'Confirmar Ativação'}
                                </Button>
                                <Button variant="secondary" onClick={() => { setIsDbModalOpen(false); setDbStatus(null); }} className="flex-1">
                                    {closeIcon} Fechar
                                </Button>
                            </div>
                        </>
                    ) : null}
                </div>
            </Modal>

            {/* Activation Invite Modal */}
            <Modal
                isOpen={isInviteModalOpen}
                onClose={() => {
                    setIsInviteModalOpen(false);
                    setInviteEmail('');
                    setInviteEmailError('');
                    setInviteEmailTouched(false);
                }}
                title="Enviar Convite de Ativação"
                loading={inviteSending}
            >
                <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Envie um email de convite para que o cliente inicie o processo de ativação da empresa.
                    </p>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Email do Cliente
                        </label>
                        <input
                            type="email"
                            placeholder="cliente@empresa.com"
                            value={inviteEmail}
                            onChange={handleInviteEmailChange}
                            onBlur={() => setInviteEmailTouched(true)}
                            disabled={inviteSending}
                            className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 transition ${
                                inviteEmailTouched && inviteEmailError
                                    ? 'border-red-500 focus:ring-red-500'
                                    : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-transparent'
                            }`}
                        />
                        {inviteEmailTouched && inviteEmailError && (
                            <p className="mt-1 text-sm text-red-500 dark:text-red-400">{inviteEmailError}</p>
                        )}
                    </div>

                    <div className="flex gap-2 pt-2">
                        <Button
                            variant="primary"
                            onClick={handleSendInvite}
                            loading={inviteSending}
                            disabled={inviteSending || !inviteEmail.trim()}
                            className="flex-1"
                        >
                            <span className="material-icons sm">send</span>
                            Enviar Email
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setIsInviteModalOpen(false);
                                setInviteEmail('');
                                setInviteEmailError('');
                                setInviteEmailTouched(false);
                            }}
                            disabled={inviteSending}
                            className="flex-1"
                        >
                            {closeIcon} Cancelar
                        </Button>
                    </div>
                </div>
            </Modal>

        </div>
    );
};

export default CompaniesPage
